import { useEditGroupClassMutation } from '~/store/services/groupClass';
import GroupClassAccordion, {
  type GroupClassFormValues,
} from './GroupClassAccordion';
import { DateFormat, FAIconName, useToastMessage } from '@equip.health/ui';
import { GROUP_CLASS_EDIT_ERROR_TEXT } from '~/lib/constants/calendar.constants';
import { isEmpty } from 'lodash';
import { GroupClassEvents } from '~/analytics/groupClassEvents';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { DateTime } from 'luxon';
import { getGroupClassRegistrationCriteriaProps } from '~/lib/util/classes/groupClasses.util';

export type EditGroupClassData = {
  selectedGroupClass?: GroupClassExtended;
  handleSuccessfulGroupClassEdit?: ({
    id,
    isViewClass,
  }: {
    id: string;
    isViewClass: boolean;
  }) => void;
};

type EditGroupClassAccordionProps = {
  dismissEditGroupClass: () => void;
  isExpanded: boolean;
  setIsExpanded: (expanded: boolean) => void;
  data: EditGroupClassData;
};

const EditGroupClassAccordion = (props: EditGroupClassAccordionProps) => {
  const { dismissEditGroupClass, isExpanded, setIsExpanded, data } = props;
  const selectedGroupClass = data.selectedGroupClass;
  const [
    editGroupClass,
    { isLoading: isEditGroupClassLoading, isSuccess: isSubmitted },
  ] = useEditGroupClassMutation();

  const { createToast } = useToastMessage();
  const { trackEvent } = useAnalytics();
  const initialValues = selectedGroupClass
    ? {
        ...selectedGroupClass,
        isPromotedInEquipApp: selectedGroupClass.isPromoted,
      }
    : undefined;

  const handleSubmit = (values: GroupClassFormValues) => {
    trackEvent(GroupClassEvents.editGroupAccordionUpdateGroupButtonClicked, {
      duration: values.duration,
      endDate: values.endDate
        ? DateTime.fromJSDate(values.endDate).toFormat(
            DateFormat.shortMonthDayYear,
          )
        : null,
      groupTitle: values.title,
      hosts: values.hosts.map((option) => ({
        name: option.firstName + ' ' + option.lastName,
        role: option.providerType,
      })),
      promoteInEquipApp: values.isPromotedInEquipApp,
      recurrence: values.frequency,
      registrationCriteria: getGroupClassRegistrationCriteriaProps(values),
      Source: AnalyticsSourceValues.CALENDAR_GROUPS_TAB,
      startDate: DateTime.fromJSDate(values.startDate).toFormat(
        DateFormat.shortMonthDayYear,
      ),
      startTime: DateTime.fromJSDate(values.startDate).toFormat(
        DateFormat.time,
      ),
    });
    editGroupClass({
      ...values,
      ageRanges: isEmpty(values.ageRanges) ? null : values.ageRanges,
      attendeeRoles: isEmpty(values.attendeeRoles)
        ? null
        : values.attendeeRoles,
      diagnoses: isEmpty(values.diagnoses) ? null : values.diagnoses,
      endDate: values.endDate,
      frequency: values.frequency,
      groupClassExternalId: selectedGroupClass?.groupClassExternalId,
      hostExternalIds: values.hosts.map((host) => host.externalId),
      isPromoted: values.isPromotedInEquipApp,
      treatmentModalities: isEmpty(values.treatmentModalities)
        ? null
        : values.treatmentModalities,
      treatmentPrograms: isEmpty(values.treatmentPrograms)
        ? null
        : values.treatmentPrograms,
    })
      .unwrap()
      .then(() => {
        data.handleSuccessfulGroupClassEdit?.({
          id: selectedGroupClass.groupClassExternalId,
          isViewClass: false,
        });
      })
      .catch(() => {
        createToast({
          icon: FAIconName.exclamationTriangleRegular,
          id: 'edit-group-toast',
          text: GROUP_CLASS_EDIT_ERROR_TEXT,
          variant: 'critical',
        });
      });
  };
  const handleViewClass = () => {
    data.handleSuccessfulGroupClassEdit?.({
      id: selectedGroupClass?.groupClassExternalId,
      isViewClass: true,
    });
  };
  return (
    <GroupClassAccordion
      initialValues={initialValues}
      isExpanded={isExpanded}
      isLoading={isEditGroupClassLoading}
      isSubmitted={isSubmitted}
      mode="edit"
      onCancel={dismissEditGroupClass}
      onSubmit={handleSubmit}
      onViewClass={handleViewClass}
      setIsExpanded={setIsExpanded}
    />
  );
};

export default EditGroupClassAccordion;
